import React, { useEffect, useState } from "react";
import { api } from "../../lib/api.js";
import { toast } from "sonner";
import { Save, RefreshCw, Bell, AlertTriangle, Plus, Trash2, Send } from "lucide-react";
import { showAdminSaveToast } from "./_shared.jsx";

const THRESHOLD_ROWS = [
  { key: "l1_hours", label: "Nível 1 · Aviso ao responsável",       unit: "horas" },
  { key: "l2_hours", label: "Nível 2 · Aviso ao supervisor",         unit: "horas" },
  { key: "l3_hours", label: "Nível 3 · Aviso ao gestor de área",     unit: "horas" },
  { key: "l4_hours", label: "Nível 4 · Aviso ao NAM",                unit: "horas" },
  { key: "l5_days",  label: "Nível 5 · Escalonamento crítico",       unit: "dias"  },
];

export default function NotificationsSettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/alert-settings");
      setCfg(data);
      setDirty(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao carregar configuração de alertas.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateThreshold = (k, v) => {
    setCfg((c) => ({ ...c, escalation_thresholds: { ...c.escalation_thresholds, [k]: Number(v) || 0 } }));
    setDirty(true);
  };

  const updateAlert = (key, patch) => {
    setCfg((c) => ({
      ...c,
      alerts: { ...c.alerts, [key]: { ...c.alerts[key], ...patch } },
    }));
    setDirty(true);
  };

  const addRecipient = (key, email) => {
    email = (email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    const cur = cfg.alerts[key].recipients || [];
    if (cur.includes(email)) {
      toast.error("Já existe");
      return;
    }
    updateAlert(key, { recipients: [...cur, email] });
  };

  const removeRecipient = (key, email) => {
    updateAlert(key, { recipients: cfg.alerts[key].recipients.filter((r) => r !== email) });
  };

  const sendTest = async (key) => {
    try {
      const { data } = await api.post("/admin/alerts/test", { alert_key: key });
      toast.success(`Teste enviado para ${data.recipient} (resend id: ${data.resend_id?.slice(0, 8)}…)`);
    } catch (e) {
      // Se o backend responder com JSON {detail: ...} mostramos a mensagem tal e qual;
      // caso contrário (ex.: 5xx via ingress → HTML), damos fallback amigável em PT.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" && detail.trim()
        ? detail
        : "Falha ao enviar alerta de teste (verifica RESEND_API_KEY e o email do destinatário).";
      toast.error(msg);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        escalation_thresholds: cfg.escalation_thresholds,
        alerts: cfg.alerts,
      };
      const { data } = await api.put("/admin/alert-settings", payload);
      setCfg(data);
      setDirty(false);
      showAdminSaveToast({ savedLabel: "Configuração de alertas guardada", warnings: data.warnings || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return <div className="text-sm text-[#6C757D] p-4">{busy ? "A carregar…" : (err || "Sem dados")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="notifications-settings-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#1F3864]" /> Notificações e Alertas
          </h2>
          <p className="text-xs text-[#6C757D] mt-0.5">
            Controla quando cada alerta dispara e quem recebe. Alterações aplicam-se imediatamente ao workflow.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={busy}
            className="px-3 py-1.5 text-xs rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white flex items-center gap-1.5"
            data-testid="alerts-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Recarregar
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="px-3 py-1.5 text-sm rounded-sm bg-[#1F3864] text-white hover:bg-[#142542] disabled:opacity-60 flex items-center gap-1.5"
            data-testid="alerts-save"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "A guardar…" : dirty ? "Guardar alterações" : "Guardar"}
          </button>
        </div>
      </div>

      {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{err}</div>}

      {/* Escalation thresholds */}
      <section className="bg-white border border-gray-200 rounded-sm p-4">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-[#FFC107]" /> Limites de escalonamento
        </h3>
        <p className="text-xs text-[#6C757D] mb-3">
          Após o prazo, o nível de escalonamento sobe conforme as horas em atraso. Esta configuração é lida em tempo real pelo workflow (`core.escalation_level`).
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {THRESHOLD_ROWS.map(({ key, label, unit }) => (
            <div key={key} data-testid={`threshold-${key}`}>
              <label className="block text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">{label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={cfg.escalation_thresholds[key] ?? ""}
                  onChange={(e) => updateThreshold(key, e.target.value)}
                  className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864] font-mono"
                  data-testid={`threshold-input-${key}`}
                />
                <span className="text-xs text-[#6C757D]">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Alerts config table */}
      <section className="bg-white border border-gray-200 rounded-sm p-4">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] mb-3">Categorias de alerta</h3>
        <div className="space-y-3">
          {Object.entries(cfg.alerts).map(([key, a]) => (
            <AlertRow
              key={key}
              alert={a}
              roleChoices={cfg.recipient_role_choices}
              onChange={(patch) => updateAlert(key, patch)}
              onAddRecipient={(email) => addRecipient(key, email)}
              onRemoveRecipient={(email) => removeRecipient(key, email)}
              onSendTest={() => sendTest(key)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function AlertRow({ alert, roleChoices, onChange, onAddRecipient, onRemoveRecipient, onSendTest }) {
  const [newEmail, setNewEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const handleTest = async () => {
    setTesting(true);
    try { await onSendTest(); } finally { setTesting(false); }
  };
  return (
    <div className="border border-gray-200 rounded-sm p-3" data-testid={`alert-row-${alert.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex-1 min-w-[240px]">
          <div className="font-heading text-sm font-semibold text-[#1A1A2E]">{alert.label}</div>
          <div className="text-[10px] font-mono text-[#94A3B8]">{alert.key}</div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <ToggleField label="Activo" checked={alert.enabled} onChange={(v) => onChange({ enabled: v })} testid={`alert-enabled-${alert.key}`} />
          <ToggleField label="Email" checked={alert.email_enabled} onChange={(v) => onChange({ email_enabled: v })} testid={`alert-email-${alert.key}`} />
          <ToggleField label="In-app" checked={alert.in_app_enabled} onChange={(v) => onChange({ in_app_enabled: v })} testid={`alert-inapp-${alert.key}`} />
          <button
            onClick={handleTest}
            disabled={testing}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-[#1F3864]/30 text-[#1F3864] hover:bg-[#1F3864] hover:text-white transition disabled:opacity-50"
            data-testid={`alert-test-${alert.key}`}
            title="Envia um email de teste para o admin actual (ignora dedup)."
          >
            <Send className="w-3 h-3" /> {testing ? "…" : "Testar"}
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Destinatário por defeito</label>
          <select
            value={alert.recipient_role}
            onChange={(e) => onChange({ recipient_role: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864] bg-white"
            data-testid={`alert-role-${alert.key}`}
          >
            {roleChoices.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Destinatários adicionais</label>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {(alert.recipients || []).length === 0 ? (
              <span className="text-xs text-[#94A3B8] italic">nenhum</span>
            ) : alert.recipients.map((email) => (
              <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F4F6F8] text-[11px] rounded-sm font-mono">
                {email}
                <button
                  onClick={() => onRemoveRecipient(email)}
                  className="text-[#DC3545] hover:text-[#a82733]"
                  data-testid={`alert-recipient-remove-${alert.key}-${email}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="email@…"
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864]"
              data-testid={`alert-recipient-input-${alert.key}`}
            />
            <button
              onClick={() => { onAddRecipient(newEmail); setNewEmail(""); }}
              className="p-1.5 bg-[#1F3864] text-white rounded-sm hover:bg-[#142542]"
              data-testid={`alert-recipient-add-${alert.key}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleField({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid={testid}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5 accent-[#1F3864]"
      />
      <span className="text-[11px] text-[#495057]">{label}</span>
    </label>
  );
}
